/**
 * Admin Tickets Page - Admin Master View
 * Admin can view all tickets from all instances, respond and manage them
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api from '../../../lib/api';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { PageHeader } from '../../../components/ui/PageHeader';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  TicketIcon,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  User,
  Headphones,
  Building2,
  Filter,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const PRIORITY_LABELS = {
  low: { label: 'Baixa', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  medium: { label: 'Média', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  high: { label: 'Alta', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400' },
  critical: { label: 'Crítica', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

const STATUS_LABELS = {
  open: { label: 'Aberto', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: AlertCircle },
  in_progress: { label: 'Em Andamento', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
  waiting_response: { label: 'Aguardando Resposta', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  resolved: { label: 'Resolvido', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: CheckCircle },
  closed: { label: 'Fechado', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: XCircle },
};

const CATEGORY_LABELS = {
  technical: 'Técnico',
  financial: 'Financeiro',
  general: 'Geral',
  bug: 'Bug/Erro',
  feature_request: 'Sugestão',
};

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, waiting_response: 0, resolved: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (priorityFilter !== 'all') params.append('priority', priorityFilter);
      
      const [ticketsRes, statsRes] = await Promise.all([
        api.get(`/tickets?${params.toString()}`),
        api.get('/tickets/stats/summary'),
      ]);
      setTickets(ticketsRes.data || []);
      setStats(statsRes.data || {});
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Erro ao carregar tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const loadTicketDetails = async (ticketId) => {
    try {
      const response = await api.get(`/tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do ticket');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;

    setSendingMessage(true);
    try {
      await api.post(`/tickets/${selectedTicket.id}/messages`, { content: newMessage });
      setNewMessage('');
      await loadTicketDetails(selectedTicket.id);
      await loadTickets();
      toast.success('Mensagem enviada');
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedTicket) return;

    try {
      await api.put(`/tickets/${selectedTicket.id}`, { status: newStatus });
      toast.success('Status atualizado');
      await loadTickets();
      await loadTicketDetails(selectedTicket.id);
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const handleUpdatePriority = async (newPriority) => {
    if (!selectedTicket) return;

    try {
      await api.put(`/tickets/${selectedTicket.id}`, { priority: newPriority });
      toast.success('Prioridade atualizada');
      await loadTickets();
      await loadTicketDetails(selectedTicket.id);
    } catch (error) {
      toast.error('Erro ao atualizar prioridade');
    }
  };

  const filteredTickets = useMemo(() => {
    if (!searchQuery) return tickets;
    const query = searchQuery.toLowerCase();
    return tickets.filter(t => 
      t.title.toLowerCase().includes(query) ||
      t.description.toLowerCase().includes(query) ||
      t.instance_name?.toLowerCase().includes(query)
    );
  }, [tickets, searchQuery]);

  const formatDate = (date) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true, locale: ptBR });
    } catch {
      return '-';
    }
  };

  const formatFullDate = (date) => {
    try {
      return format(new Date(date), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return '-';
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets-page">
      <PageHeader
        title="Central de Suporte"
        icon={Headphones}
        subtitle={`${stats.total} ticket(s) no total`}
        actions={
          <Button variant="outline" onClick={loadTickets} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className={`cursor-pointer transition-all ${statusFilter === 'all' ? 'ring-2 ring-primary' : 'hover:border-primary'}`} onClick={() => setStatusFilter('all')}>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{stats.total || 0}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${statusFilter === 'open' ? 'ring-2 ring-green-500' : 'hover:border-green-500'}`} onClick={() => setStatusFilter('open')}>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">{stats.open || 0}</p>
              <p className="text-sm text-muted-foreground">Abertos</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${statusFilter === 'in_progress' ? 'ring-2 ring-blue-500' : 'hover:border-blue-500'}`} onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.in_progress || 0}</p>
              <p className="text-sm text-muted-foreground">Em Andamento</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${statusFilter === 'waiting_response' ? 'ring-2 ring-yellow-500' : 'hover:border-yellow-500'}`} onClick={() => setStatusFilter('waiting_response')}>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats.waiting_response || 0}</p>
              <p className="text-sm text-muted-foreground">Aguardando</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer transition-all ${statusFilter === 'resolved' ? 'ring-2 ring-emerald-500' : 'hover:border-emerald-500'}`} onClick={() => setStatusFilter('resolved')}>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-emerald-600">{(stats.resolved || 0) + (stats.closed || 0)}</p>
              <p className="text-sm text-muted-foreground">Resolvidos</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por título, descrição ou instância..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Prioridade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="critical">Crítica</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="medium">Média</SelectItem>
                <SelectItem value="low">Baixa</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Ticket List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <TicketIcon className="h-5 w-5" />
                Tickets ({filteredTickets.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[600px]">
                {filteredTickets.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <TicketIcon className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhum ticket encontrado</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredTickets.map(ticket => {
                      const status = STATUS_LABELS[ticket.status] || STATUS_LABELS.open;
                      const priority = PRIORITY_LABELS[ticket.priority] || PRIORITY_LABELS.medium;
                      const StatusIcon = status.icon;
                      
                      return (
                        <button
                          key={ticket.id}
                          onClick={() => loadTicketDetails(ticket.id)}
                          className={`w-full p-4 text-left hover:bg-muted/50 transition-colors ${
                            selectedTicket?.id === ticket.id ? 'bg-muted border-l-4 border-l-primary' : ''
                          }`}
                          data-testid={`ticket-${ticket.id}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <StatusIcon className={`h-4 w-4 shrink-0 ${
                                  ticket.status === 'open' ? 'text-green-600' :
                                  ticket.status === 'in_progress' ? 'text-blue-600' :
                                  ticket.status === 'waiting_response' ? 'text-yellow-600' :
                                  'text-gray-400'
                                }`} />
                                <p className="font-medium truncate">{ticket.title}</p>
                              </div>
                              
                              {ticket.instance_name && (
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <Building2 className="h-3 w-3" />
                                  {ticket.instance_name}
                                </p>
                              )}
                              
                              <div className="flex items-center gap-2 mt-2">
                                <Badge className={priority.color} variant="secondary">
                                  {priority.label}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {CATEGORY_LABELS[ticket.category]}
                                </Badge>
                              </div>
                              
                              <p className="text-xs text-muted-foreground mt-2">
                                {formatDate(ticket.created_at)}
                              </p>
                            </div>
                            
                            {ticket.messages?.length > 0 && (
                              <Badge className="shrink-0">
                                {ticket.messages.length}
                              </Badge>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Ticket Details */}
        <div className="lg:col-span-2">
          {selectedTicket ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                      <Building2 className="h-4 w-4" />
                      <span>{selectedTicket.instance_name || 'Sem instância'}</span>
                      <span>•</span>
                      <span>Aberto por {selectedTicket.created_by_name}</span>
                      <span>•</span>
                      <span>{formatFullDate(selectedTicket.created_at)}</span>
                    </div>
                    <CardTitle>{selectedTicket.title}</CardTitle>
                    <CardDescription className="mt-2">
                      {selectedTicket.description}
                    </CardDescription>
                  </div>
                </div>
                
                {/* Admin Controls */}
                <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Status:</Label>
                    <Select value={selectedTicket.status} onValueChange={handleUpdateStatus}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">Aberto</SelectItem>
                        <SelectItem value="in_progress">Em Andamento</SelectItem>
                        <SelectItem value="waiting_response">Aguardando Resposta</SelectItem>
                        <SelectItem value="resolved">Resolvido</SelectItem>
                        <SelectItem value="closed">Fechado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Label className="text-sm">Prioridade:</Label>
                    <Select value={selectedTicket.priority} onValueChange={handleUpdatePriority}>
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baixa</SelectItem>
                        <SelectItem value="medium">Média</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                        <SelectItem value="critical">Crítica</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                {/* Messages */}
                <ScrollArea className="h-[350px] pr-4">
                  <div className="space-y-4">
                    {selectedTicket.messages?.length === 0 ? (
                      <div className="text-center text-muted-foreground py-8">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                        <p>Nenhuma mensagem ainda</p>
                        <p className="text-sm">Seja o primeiro a responder!</p>
                      </div>
                    ) : (
                      selectedTicket.messages?.map(msg => (
                        <div
                          key={msg.id}
                          className={`flex gap-3 ${
                            msg.user_role === 'admin-master' ? 'flex-row-reverse' : ''
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            msg.user_role === 'admin-master' 
                              ? 'bg-primary text-primary-foreground' 
                              : 'bg-muted'
                          }`}>
                            {msg.user_role === 'admin-master' ? (
                              <Headphones className="h-4 w-4" />
                            ) : (
                              <User className="h-4 w-4" />
                            )}
                          </div>
                          <div className={`flex-1 max-w-[80%] ${
                            msg.user_role === 'admin-master' ? 'text-right' : ''
                          }`}>
                            <div className={`rounded-lg p-3 ${
                              msg.user_role === 'admin-master'
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {msg.user_name} • {formatDate(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </ScrollArea>

                {/* Reply Box */}
                {selectedTicket.status !== 'closed' && (
                  <div className="mt-4 pt-4 border-t">
                    <Textarea
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Digite sua resposta..."
                      rows={3}
                      className="mb-2"
                      data-testid="admin-message-input"
                    />
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setNewMessage('');
                          handleUpdateStatus('waiting_response');
                        }}
                      >
                        Marcar como Aguardando
                      </Button>
                      <Button onClick={handleSendMessage} disabled={sendingMessage || !newMessage.trim()}>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Resposta
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center h-[600px] text-muted-foreground">
                <Headphones className="h-16 w-16 mb-4 opacity-30" />
                <p className="text-lg">Central de Suporte</p>
                <p className="text-sm mt-1">Selecione um ticket para responder</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
